import "./lib/initializeNavigationPrivacy";
import { validateEnv } from "./lib/validateEnv";
import { preloadSentry } from "./lib/sentryLazy";

// Validate required configuration after URL privacy cleanup and before rendering.
validateEnv();

import { createRoot } from "react-dom/client";
import { SpeedInsights } from "@vercel/speed-insights/react";
import App from "./app/App.tsx";
import "./styles/index.css";
// Article content is also prerendered in the initial HTML. Load its scoped
// styles with the shell instead of making route hydration await a CSS chunk.
import "./styles/ghost-content.css";

// ── Defer non-critical JS until after first paint ────────────────────────
// Sentry, PostHog, FB Pixel, and Google Ads were previously imported and
// executed synchronously before createRoot(), blocking the main thread for
// ~200-400ms on mobile. Moving them to requestIdleCallback ensures they
// load after the first content paint without affecting LCP.
function initMonitoring() {
  preloadSentry();
  // Cookieless aggregate page views; runs without the cookie banner.
  import("./lib/vercelWebAnalytics").then((m) => m.initVercelWebAnalytics());
  let consent: string | null = null;
  try {
    consent = localStorage.getItem("fairpath_cookie_consent");
  } catch {
    // Storage unavailable: analytics remains off.
  }
  if (consent === "accepted") {
    import("./lib/posthog").then((m) => m.initPostHog());
    import("./lib/fbpixel").then((m) => m.initFacebookPixel());
    import("./lib/gtag").then((m) => m.initGoogleAds());
    import("./lib/openaiAds").then((m) => m.initOpenAIAdsPixel());
  }
}

if ("requestIdleCallback" in window) {
  requestIdleCallback(initMonitoring, { timeout: 3000 });
} else {
  // Safari fallback — requestIdleCallback not supported
  setTimeout(initMonitoring, 1);
}

// ── Stale chunk handler (FAIRPATH-1D) ──────────────────────────────────────
// After a Vite deployment, old chunk hashes are invalidated but users with
// cached HTML still request them. The ErrorBoundary catches failures inside
// the React tree, but dynamic import errors during route transitions can fire
// before React mounts. This global handler catches those.
const CHUNK_RELOAD_KEY = "fp_chunk_reload";

function isStaleChunkError(msg: string | undefined): boolean {
  if (!msg) return false;
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk")
  );
}

function handleStaleChunkReload(): void {
  if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
    window.location.reload();
  }
  // Already reloaded once — let ErrorBoundary UI handle it
}

// Catches errors thrown synchronously (e.g., <script> load failures)
window.addEventListener("error", (event) => {
  if (isStaleChunkError(event.message)) {
    event.preventDefault(); // Prevent Sentry from capturing expected post-deploy errors
    handleStaleChunkReload();
  }
});

// Catches rejected dynamic import() promises and stale SW errors
window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason;
  const msg =
    reason instanceof Error ? reason.message : String(reason ?? "");

  if (isStaleChunkError(msg)) {
    event.preventDefault(); // Prevent Sentry from capturing expected post-deploy errors
    handleStaleChunkReload();
    return;
  }

  // Suppress InvalidStateError from ServiceWorker lifecycle — browser-managed
  // behavior that is not actionable by the user. (FAIRPATH-1K)
  if (
    reason instanceof DOMException &&
    reason.name === "InvalidStateError" &&
    msg.includes("ServiceWorker")
  ) {
    event.preventDefault();
  }
});

// Unregister all ServiceWorkers (FAIRPATH-1K) — the self-destruct sw.js has
// served its purpose (clearing broken caches from the old SW that returned
// index.html for JS chunks). Continuing to register it causes
// InvalidStateError when the browser can't update a SW already in a terminal
// state. FairPath is a Vite SPA with no offline requirements — clean slate.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      registration.unregister();
    }
  });
}

createRoot(document.getElementById("root")!).render(
  <>
    <App />
    <SpeedInsights />
  </>
);
